import { useClientPortalAPI } from "@certn/api";
import { Stack } from "@certn/cascadia/foundations/grid-layout/Stack";
import { Text } from "@certn/cascadia/foundations/typography/Text";
import {
  Card,
  GhostIconButton,
  LinkButton,
  List,
  ListItem,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useState, type ReactElement } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { useUser } from "../../hooks/useUser";
import { DASHBOARD_SUMMARY_PATH } from "../monitoring/useMonitoringApi";
import { useCanEnrollInMonitoring } from "../monitoring/useCanEnrollInMonitoring";
import { useCanViewMonitoring } from "../monitoring/useCanViewMonitoring";

const DISMISS_STORAGE_KEY = "monitoring_eligibility_banner_dismissed";
const MONITORING_ENROLL_PATH = "/monitoring/enroll";
const AU_RIGHT_TO_WORK = "AU_RIGHT_TO_WORK_1";

type EligibleMonitoringOffer = {
  type: string;
  label: string;
  eligible_applicant_count: number;
  price: string;
  currency: string;
};

function isDismissedFor(userId: string | undefined) {
  if (typeof window === "undefined" || !userId) {
    return false;
  }
  return (
    window.localStorage.getItem(`${DISMISS_STORAGE_KEY}_${userId}`) === "true"
  );
}

function formatMonitoringPrice(
  price: string | null | undefined,
  currency: string,
  locale: string
) {
  if (price == null || price === "") {
    return null;
  }

  const numericPrice = Number(price);
  if (!Number.isFinite(numericPrice)) {
    return null;
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
    }).format(numericPrice);
  } catch {
    return null;
  }
}

export function MonitoringEligibilityBanner() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { clientPortalEnableContinuousMonitoring } = useFlags();
  const canViewMonitoring = useCanViewMonitoring();
  const canEnroll = useCanEnrollInMonitoring();
  const { details: userDetails } = useUser();
  const userId = userDetails?.id;
  const { data, isLoading, error } = useClientPortalAPI(
    clientPortalEnableContinuousMonitoring && canViewMonitoring
      ? DASHBOARD_SUMMARY_PATH
      : null
  );
  const [sessionDismissedUserId, setSessionDismissedUserId] = useState<
    string | undefined
  >();
  const isDismissed =
    sessionDismissedUserId === userId || isDismissedFor(userId);

  if (
    !clientPortalEnableContinuousMonitoring ||
    !canViewMonitoring ||
    isLoading ||
    error ||
    !data ||
    !userId ||
    isDismissed
  ) {
    return null;
  }

  const eligibleCount = data.eligible_count ?? 0;
  if (eligibleCount <= 0) {
    return null;
  }

  const offers = (data.eligible_monitoring_offers ??
    []) as EligibleMonitoringOffer[];
  const isRightToWorkOnly =
    offers.length === 0 ||
    (offers.length === 1 && offers[0]?.type === AU_RIGHT_TO_WORK);
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const rightToWorkOffer = offers[0];
  const rightToWorkPrice = isRightToWorkOnly
    ? formatMonitoringPrice(
        rightToWorkOffer?.price ?? data.monitoring_price,
        rightToWorkOffer?.currency ?? data.monitoring_currency ?? "",
        locale
      )
    : null;
  const countLink = canEnroll ? (
    <Link
      to={MONITORING_ENROLL_PATH}
      className={styles.eligibleCountLink}
      data-testid="monitoring-eligibility-banner-eligible-count-link"
    />
  ) : (
    <span data-testid="monitoring-eligibility-banner-eligible-count" />
  );

  return (
    <Card
      className={styles.card}
      testID="monitoring-eligibility-banner"
      hideBorder
    >
      <Stack gap="space-4" className={styles.content}>
        <Stack gap="space-2" orientation="horizontal" className={styles.header}>
          <Text style="global/heading-large">
            {isRightToWorkOnly
              ? t("Australian Right to Work monitoring")
              : t("Continuous Check Monitoring")}
          </Text>
          <GhostIconButton
            aria-label={t("Close")}
            size="small"
            icon="close"
            testID="monitoring-eligibility-banner-close"
            onPress={() => {
              window.localStorage.setItem(
                `${DISMISS_STORAGE_KEY}_${userId}`,
                "true"
              );
              setSessionDismissedUserId(userId);
            }}
          />
        </Stack>
        {isRightToWorkOnly ? (
          <RightToWorkBannerBody
            eligibleCount={eligibleCount}
            countLink={countLink}
            formattedPrice={rightToWorkPrice}
          />
        ) : (
          <MultiCheckBannerBody
            eligibleCount={eligibleCount}
            countLink={countLink}
            offers={offers}
            locale={locale}
          />
        )}
        {canEnroll && (
          <LinkButton
            testID="monitoring-eligibility-banner-cta"
            onPress={() => navigate(MONITORING_ENROLL_PATH)}
          >
            {t("Enroll applicants")}
          </LinkButton>
        )}
      </Stack>
    </Card>
  );
}

function RightToWorkBannerBody({
  eligibleCount,
  countLink,
  formattedPrice,
}: {
  eligibleCount: number;
  countLink: ReactElement;
  formattedPrice: string | null;
}) {
  const { t } = useTranslation();

  return (
    <>
      <Stack gap="space-3">
        <Text color="text-weak" style="global/body-secondary">
          <Trans
            i18nKey="You can now enroll your applicants in continuous monitoring. You have <1>{{count}}</1> applicants eligible for ongoing verification — including those on temporary or conditional visas whose work rights may change."
            count={eligibleCount}
            values={{ count: eligibleCount }}
            components={{ 1: countLink }}
          />
        </Text>
        <Text color="text-weak" style="global/body-secondary">
          {t(
            "Australian employers have legal obligations to verify and maintain employees' right to work. Ongoing monitoring helps you stay compliant throughout employment, not just at hire."
          )}
        </Text>
      </Stack>
      <Text color="text-weak" style="global/body-secondary">
        {formattedPrice
          ? t(
              "We re-check work rights every 2 weeks for a year and alert you when anything changes. {{price}} per employee per year.",
              { price: formattedPrice }
            )
          : t(
              "We re-check work rights every 2 weeks for a year and alert you when anything changes."
            )}
      </Text>
    </>
  );
}

function MultiCheckBannerBody({
  eligibleCount,
  countLink,
  offers,
  locale,
}: {
  eligibleCount: number;
  countLink: ReactElement;
  offers: EligibleMonitoringOffer[];
  locale: string;
}) {
  const { t } = useTranslation();

  return (
    <>
      <Stack gap="space-3">
        <Text color="text-weak" style="global/body-secondary">
          <Trans
            i18nKey="Keep your workforce compliant after hire. We re-check enrolled checks every 2 weeks for 12 months and alert you as soon as anything changes. <1>{{count}}</1> applicant eligible for continuous monitoring."
            count={eligibleCount}
            values={{ count: eligibleCount }}
            components={{ 1: countLink }}
          />
        </Text>
        {offers.length > 0 && (
          <Stack gap="space-1">
            <Text color="text-weak" style="global/body-secondary">
              {t("Available checks (price per applicant, per check):")}
            </Text>
            <List
              type="unordered"
              size="secondary"
              testID="monitoring-eligibility-banner-offers"
            >
              {offers.map((offer) => {
                const formattedPrice = formatMonitoringPrice(
                  offer.price,
                  offer.currency,
                  locale
                );
                return (
                  <ListItem
                    key={offer.type}
                    size="secondary"
                    color="text-weak"
                    testID={`monitoring-eligibility-banner-offer-${offer.type}`}
                  >
                    {formattedPrice
                      ? t("{{checkType}} — {{price}} · {{count}} eligible", {
                          checkType: offer.label,
                          price: formattedPrice,
                          count: offer.eligible_applicant_count,
                        })
                      : t("{{checkType}} · {{count}} eligible", {
                          checkType: offer.label,
                          count: offer.eligible_applicant_count,
                        })}
                  </ListItem>
                );
              })}
            </List>
          </Stack>
        )}
      </Stack>
    </>
  );
}

const styles = {
  card: css({
    marginBottom: "var(--space-4)",
    borderRadius: "var(--border-radius-3)",
    backgroundColor: "var(--background-accent-tofino-weak) !important",
  }),
  content: css({
    padding: "var(--space-5)",
  }),
  header: css({
    alignItems: "flex-start",
    justifyContent: "space-between",
  }),
  eligibleCountLink: css({
    color: "inherit",
    textDecoration: "underline",
    cursor: "pointer",
  }),
};
